import { useState } from 'react'
import {
  Button,
  CollapsibleCard,
  IconButton,
  Input,
  OrderButtons,
  SectionLabel,
} from '../../../design/components'
import type { ScenePanelsSection } from '../types'
import type { SceneEditing } from '../useSceneEditing'
import { PageRow } from './PageRow'
import { moved } from '../../../lib/order'
import styles from './play.module.css'

/**
 * The scene's comic panels, in order, with the pages under each.
 *
 * The card owns the panels — adding, rewording, removing and reordering — and each page
 * under them owns its own generation (`PageRow`), because a job is per page and a shared
 * progress line would say nothing useful about which one was running.
 *
 * Reordering sends a complete list of ids, because the endpoint rejects a partial one.
 */
export function PanelsCard({
  sceneId,
  panels,
  editing,
}: {
  sceneId: number
  panels: ScenePanelsSection | undefined
  editing: SceneEditing
}) {
  const [adding, setAdding] = useState(false)
  const [description, setDescription] = useState('')

  if (panels === undefined) return null

  const all = panels.panels ?? []
  const pageCount = all.reduce((total, panel) => total + panel.pages.length, 0)

  return (
    <CollapsibleCard
      variant="magic"
      label="Panels"
      summary={
        all.length === 0
          ? 'none yet'
          : `${all.length} ${all.length === 1 ? 'panel' : 'panels'} · ${pageCount} ${pageCount === 1 ? 'page' : 'pages'}`
      }
      action={
        editing.canEdit && !adding ? (
          <IconButton label="Add a panel" icon="add" onClick={() => setAdding(true)} />
        ) : undefined
      }
    >
      {all.length === 0 && !adding && <p className={styles.empty}>No panels written yet.</p>}

      {all.map((panel, index) => (
        <div key={panel.id} className={styles.row}>
          <div className={styles.rowHead}>
            <span className={styles.rowName}>Panel {index + 1}</span>
            {editing.canEdit && (
              <span className={styles.orderButtons}>
                <OrderButtons
                  label={`panel ${index + 1}`}
                  index={index}
                  total={all.length}
                  disabled={editing.reorderPanels.isPending}
                  onMove={(to) =>
                    editing.reorderPanels.mutate({
                      ids: moved(
                        all.map((entry) => entry.id),
                        index,
                        to,
                      ),
                    })
                  }
                />
                <Button
                  variant="danger"
                  disabled={editing.removePanel.isPending}
                  onClick={() => editing.removePanel.mutate({ panelId: panel.id })}
                >
                  Remove
                </Button>
              </span>
            )}
          </div>

          <p className={styles.rowText}>{panel.description}</p>

          {panel.pages.length > 0 && (
            <div className={styles.subBlock}>
              <SectionLabel tone="muted" micro>
                Pages
              </SectionLabel>
              <div className={styles.subList}>
                {panel.pages.map((page, pageIndex) => (
                  <PageRow
                    key={page.id}
                    sceneId={sceneId}
                    page={page}
                    index={pageIndex}
                    canGenerate={editing.canEdit}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      ))}

      {editing.canEdit && adding && (
        <form
          className={styles.inlineForm}
          onSubmit={(event) => {
            event.preventDefault()
            editing.addPanel.mutate(
              { description },
              {
                onSuccess: () => {
                  setDescription('')
                  setAdding(false)
                },
              },
            )
          }}
        >
          <Input
            label="What happens in this panel"
            autoFocus
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <div className={styles.actions}>
            <Button variant="secondary" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={editing.addPanel.isPending || description.trim() === ''}
            >
              {editing.addPanel.isPending ? 'Adding…' : 'Add'}
            </Button>
          </div>
        </form>
      )}
    </CollapsibleCard>
  )
}
